import { CalendarPlus, Feather, ImagePlus, NotebookPen, Plus, ScanText } from 'lucide-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { ActionMenu, type Action } from '@/components/ActionMenu'
import type { Me } from '@/lib/api'
import type { Family } from '@/lib/families'
import { useFrame } from '@/lib/frame'
import { handOffToPhotos } from '@/lib/handoff'
import { PHOTO_TYPES } from '@/lib/photos'
import { mySections } from '@/lib/sections'
import { posts } from '../../../shared/uses'

const plus =
  'grid size-11 shrink-0 place-items-center rounded-full text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60 android:size-12 nav:size-10 nav:transition-colors nav:hover:bg-secondary nav:active:opacity-100'

// Home's "+": start anything from here without going to its page first (Dallas, 2026-10-18), for each section this
// circle has on and you add to yourself (not where you've picked Just see). Each one goes on as its own "+" does there:
// photos are picked here and Photos asks which album, a moment or recipe opens its form, a date opens the calendar's.
// It's the usual "+" at the top right, leading Home's view and Filter there; on a phone the logo is at the left.
export function HomeAdd({ family, me }: { family: Family; me: Me }) {
  const { actionSlot } = useFrame()
  const navigate = useNavigate()
  const [picker, setPicker] = useState<HTMLInputElement | null>(null)
  const sections = mySections(me, family)
  const adds = (s: 'photos' | 'moments' | 'recipes') => sections[s] && posts(me.uses, s)
  const base = `/circle/${family.id}`
  const actions: Action[] = [
    ...(adds('photos') ? [{ label: 'Add photos', icon: ImagePlus, onSelect: () => picker?.click() }] : []),
    ...(adds('moments')
      ? [{ label: 'Write a moment', icon: Feather, onSelect: () => navigate(`${base}/moments/new`) }]
      : []),
    ...(adds('recipes')
      ? [
          { label: 'Scan a recipe card', icon: ScanText, onSelect: () => navigate(`${base}/recipes/new`) },
          { label: 'Write a recipe', icon: NotebookPen, onSelect: () => navigate(`${base}/recipes/new?write=1`) },
        ]
      : []),
    ...(sections.calendar
      ? [{ label: 'Add a date', icon: CalendarPlus, onSelect: () => navigate(`${base}/calendar?add=1`) }]
      : []),
  ]
  if (!actionSlot || actions.length === 0) return null
  return (
    <>
      <input
        ref={setPicker}
        type="file"
        accept={PHOTO_TYPES}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="home-add-photos"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (!files.length) return
          handOffToPhotos(family.id, files)
          navigate(`${base}/photos`)
        }}
      />
      {createPortal(
        <ActionMenu
          title="Add"
          trigger={
            <button type="button" aria-label="Add" title="Add" className={plus}>
              <Plus className="size-6.5 stroke-[1.75]" aria-hidden />
            </button>
          }
          groups={[actions]}
          align="end"
        />,
        actionSlot,
      )}
    </>
  )
}
